import type L from "leaflet";
import type maplibregl from "maplibre-gl";

/**
 * Leaflet 経由で生成された maplibre-gl 由来のレイヤーへアクセスするための
 * 内部 API ラッパー。
 *
 * Leaflet が公開している型には maplibre-gl のインスタンス参照は含まれない
 * ため、_glMap という private プロパティを読みに行く必要がある。地図出力時の
 * 再描画キックや、印刷モードでの resize 呼び出し等で参照する。
 */

interface GlMapHolder {
  _glMap?: maplibregl.Map;
}

export const getGlMap = (
  layer: L.Layer | undefined | null
): maplibregl.Map | null => {
  if (!layer) return null;
  const glMap = (layer as GlMapHolder)._glMap;
  return glMap ?? null;
};

/**
 * maplibre-gl の Map が破棄(remove)済みでないか（操作しても安全か）を判定する。
 *
 * maplibre-gl は `map.remove()` の中で内部の `style` を破棄し、`_removed` を立てる。
 * 破棄後に `getLayer` / `getSource` などを呼ぶと、内部実装が
 * `this.style.getLayer(...)` を参照するため
 * "Cannot read properties of undefined (reading 'getLayer')" で例外になる。
 *
 * 画面遷移で地図ごとアンマウントされる際、親(MaplibreLayerContext)が
 * `layer.remove()` で maplibre を破棄した後に、子レイヤーのクリーンアップ
 * (useEffect の return)が走るケースを安全に無視するために使う。地図ごと
 * 破棄される場合は個別のレイヤー/ソース削除は不要なのでスキップしてよい。
 *
 * 主判定は破棄フラグ `_removed` を使い、加えて `style` の破棄も確認する。
 * `_removed` は公開 API ではないため、将来 maplibre 側で名称が変わっても
 * `style` の破棄で破棄を検知できるようにして二重に守っている。
 */
export const isMaplibreMapAlive = (
  map: maplibregl.Map | null
): map is maplibregl.Map => {
  if (!map) return false;
  const internal = map as unknown as {
    _removed?: boolean;
    style?: unknown;
  };
  return internal._removed !== true && internal.style != null;
};
